/**
 * Safe translation utilities to handle Symbol values from i18next
 *
 * During development or when translations are not yet loaded, i18next
 * may return Symbol values instead of strings, which causes errors
 * in components like react-helmet that expect string values.
 */

/**
 * Safely convert any value to a string, handling Symbol values from i18next
 * @param {*} value - The value to convert
 * @returns {string} - The safely converted string
 */
export const safeString = (value) => {
    if (typeof value === 'symbol') {
        // Extract the key from Symbol(key) and return it
        const symbolStr = String(value);
        const match = symbolStr.match(/^Symbol\((.*)\)$/);
        return match ? match[1] : symbolStr;
    }
    if (value === null || value === undefined) {
        return '';
    }
    return String(value);
};

/**
 * Create a safe translation wrapper for the i18next t function
 * @param {Function} t - The i18next translation function
 * @returns {Function} - A safe version of the translation function
 */
export const createSafeT = (t) => {
    return (key, defaultValue = '') => {
        try {
            const result = t(key);
            return safeString(result || defaultValue || key);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`Translation error for key "${key}":`, error);
            }
            return defaultValue || key;
        }
    };
};

/**
 * React hook for safe translations
 * @param {Object} translationHook - The result of useTranslation() hook
 * @returns {Function} - A safe translation function
 */
export const useSafeTranslation = (translationHook) => {
    const { t } = translationHook;
    return createSafeT(t);
};
